import { useEffect, useState, useCallback, useMemo } from "react";
import { Button } from "react-bootstrap";

import { api_without_cancellation } from "../../api/api";
import { getCaseId } from "../../Utils/helper";
import GenericModalComponent from "../common/Modal/Modal";
import { useDocumentModal } from "../common/CustomModal/CustomModalContext";
import ResponsiveTabContainer from "../DocPage/new-doc-page/ResponsiveTabContainer";
import useTabsData from "../DocPage/new-doc-page/hooks/useGetTabsData";

import "./assemble-document.css";

const getDocumentSlotLabel = (documentSlot) => {
  if (!documentSlot) return "";

  const { slot_number: slotNumber, slot_name: slotName } = documentSlot;
  const hasSlotNumber = slotNumber !== null && slotNumber !== undefined && slotNumber !== "";

  if (!hasSlotNumber && !slotName) return "";
  if (Number(slotNumber) === 0) return [slotName, "Generally"].filter(Boolean).join(" ");
  if (hasSlotNumber && !slotName) return `${slotNumber}. Empty`;

  return hasSlotNumber ? `${slotNumber}. ${slotName}` : slotName;
};

function AssembleDocument({ handleClose, show }) {
  const origin = process.env.REACT_APP_BACKEND_URL;
  const currentCaseId = getCaseId();
  const [documents, setDocuments] = useState([]);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [selectedPage, setSelectedPage] = useState("all");
  const { pageSlots, allCount } = useTabsData();

  const { showAssembleDocumentModal } = useDocumentModal();

  const handleDocPreview = () => {
    handleClose();

    const assemble_docs = JSON.stringify(selectedDocs);

    showAssembleDocumentModal(assemble_docs);
  };

  const isSaveDisabled = useMemo(() => {
    return selectedDocs.length < 1;
  }, [selectedDocs]);

  const handleCheckboxChange = useCallback(
    (doc) => {
      setSelectedDocs(
        (prevDocs) =>
          prevDocs.includes(doc.id)
            ? prevDocs.filter((id) => id !== doc.id) // Remove the ID if already selected
            : [...prevDocs, doc.id] // Add the ID if not selected
      );
    },
    [setSelectedDocs]
  );

  const fetchData = useCallback(async () => {
    try {
      const response = await api_without_cancellation.get(
        `${origin}/api/doc-page/doc-page-popup-api/`,
        {
          params: {
            page_id: selectedPage === "all" ? "all" : selectedPage,
            case_id: currentCaseId ? currentCaseId : "",
          },
        }
      );
      if (response.status === 200) {
        setDocuments(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
    }
  }, [currentCaseId, origin, selectedPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const pageIconByName = useMemo(
    () => new Map(pageSlots.map((page) => [page.name, page.page_icon])),
    [pageSlots]
  );

  const bodyContent = useMemo(
    () => (
      <>
        <div className="assemble-page-tabs-shell">
          <ResponsiveTabContainer
            categoriesOnly
            onSelect={(pageId) => setSelectedPage(pageId)}
            data={pageSlots}
            allCount={allCount}
            activeTab={selectedPage}
          />
        </div>

        <div className="custom-tab">
          <div className="table-responsive table--no-card rounded-0 border-0 assemble-doc-table-wrap">
            <table className="table table-borderless table-striped table-earning has-height-25 generate-doc-table assemble-doc-table">
              <thead
                style={{
                  position: "sticky",
                  top: "0px",
                  zIndex: "10",
                  width: "100%",
                  // background: "white",
                }}
              >
                <tr>
                  <th
                    className="td-autosize"
                    style={{
                      width: "25px",
                      minWidth: "25px",
                      maxWidth: "25px",
                    }}
                  />
                  <th
                    style={{
                      width: "25px",
                      minWidth: "25px",
                      maxWidth: "25px",
                    }}
                  />
                  <th className="td-autosize text-center">Page</th>
                  <th />
                  <th className=" td-autosize text-center">Doc Slot</th>
                  <th />
                  <th className=" td-autosize text-center">Name</th>
                  <th />
                  <th className=" td-autosize text-center">Pages</th>
                  <th
                    style={{
                      width: "5px",
                      minWidth: "5px",
                      maxWidth: "5px",
                    }}
                  />
                </tr>
              </thead>
              <tbody id="table-body-cat" className="generate-doc-body">
                {documents
                  // ?.filter((obj) => obj.page_count) // Filter out objects where page_count is falsy (null, undefined, 0, etc.)
                  .map((obj, index) => (
                    <tr key={obj.id}>
                      <td
                        style={{
                          width: "25px",
                          minWidth: "25px",
                          maxWidth: "25px",
                        }}
                      >
                        {index + 1}
                      </td>
                      <td
                        style={{
                          width: "25px",
                          minWidth: "25px",
                          maxWidth: "25px",
                        }}
                      >
                        <div className="justify-content-center">
                          <input
                            className="form-check template-checkbox mb-0"
                            type="checkbox"
                            style={{
                              minHeight: "13px",
                              cursor: "pointer",
                            }}
                            checked={selectedDocs.includes(obj.id)}
                            onChange={() => handleCheckboxChange(obj)}
                          />
                        </div>
                      </td>
                      <td className="text-nowrap text-start">
                        <div className="assemble-page-cell">
                          {(obj?.page?.page_icon ||
                            obj?.page_icon ||
                            pageIconByName.get(obj.page_name)) && (
                            <img
                              src={
                                obj?.page?.page_icon ||
                                obj?.page_icon ||
                                pageIconByName.get(obj.page_name)
                              }
                              className="assemble-page-cell-icon"
                              alt=""
                            />
                          )}
                          <span>{obj.page_name || obj?.page?.name || ""}</span>
                        </div>
                      </td>
                      <td />
                      <td className="text-nowrap text-start">
                        {getDocumentSlotLabel(obj.document_slot)}
                      </td>
                      <td />
                      <td className="text-nowrap">{obj.file_name ? obj.file_name : ""}</td>
                      <td />
                      <td className="text-nowrap">{obj.page_count ? obj.page_count : ""}</td>
                      <td />
                    </tr>
                  ))}
                {Array.from({ length: Math.max(0, 15 - documents?.length) }, (_, index) => (
                  <tr key={`empty-row-${index}`} aria-hidden="true">
                    <td colSpan={10} style={{ height: "35px" }} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="d-flex justify-content-between px-1 pt-1">
          <Button
            variant="secondary"
            onClick={handleClose}
            className="height-25 d-flex align-items-center justify-content-center"
          >
            Cancel
          </Button>
          <div className="d-flex space-x-5">
            <Button
              variant="primary"
              onClick={() => handleDocPreview()}
              disabled={isSaveDisabled}
              className="height-25 d-flex align-items-center justify-content-center"
            >
              Assemble Documents
            </Button>
          </div>
        </div>
      </>
    ),

    [
      allCount,
      documents,
      handleClose,
      isSaveDisabled,
      pageIconByName,
      pageSlots,
      selectedDocs,
      selectedPage,
    ]
  );

  return (
    <GenericModalComponent
      show={show}
      handleClose={handleClose}
      title={
        <>
          <i className="ic ic-25 ic-generate-document m-r-5" /> Select Documents To Assemble
        </>
      }
      height="653px"
      bodyContent={bodyContent}
      dialogClassName="modal-dialog-centered genrate-docs-modal"
      titleClassName="tfbp-modal-title"
      modalBodyClassName="p-0"
      headerClassName="text-center p-0 bg-primary popup-heading-color justify-content-center"
    />
  );
}

export default AssembleDocument;
